import { sortBy, keyBy } from 'lodash-es';
import * as C from '../constants';
import router from '../../../router';
import CoreService from '../../../services/core-service';
import TriggerService from '../../../services/trigger-service';
import Snackbar from '../../../services/snackbar-service';

export default {
  /**
   * @param commit
   */
  resetState({ commit }) {
    commit(C.TRIGGERS_RESET);
  },

  /**
   * @param commit
   * @param dispatch
   * @param domain
   */
  setTemplateDomain({ commit, dispatch }, domain) {
    commit(C.TRIGGERS_TEMPLATES_DOMAIN_SET, domain);
    dispatch('getTemplates', domain.id);
  },

  /**
   * @param commit
   * @param triggerType
   */
  setTemplateTriggerType({ commit }, triggerType) {
    commit(C.TRIGGERS_TEMPLATES_TRIGGER_TYPE_SET, triggerType);
  },

  /**
   * @param commit
   */
  loadDomains({ commit }) {
    commit(C.TRIGGERS_DOMAINS_PENDING);
    return CoreService.getDomains().then((res) => {
      if (res.status === 204) {
        commit(C.TRIGGERS_DOMAINS_NO_CONTENT);
      } else {
        commit(C.TRIGGERS_DOMAINS_SUCCESS, sortBy(res.data, ['brandId', 'name']));
      }
    }).catch(() => {
      commit(C.TRIGGERS_DOMAINS_FAILURE);
    });
  },

  /**
   * @param commit
   */
  getTriggerTypes({ commit }) {
    commit(C.TRIGGERS_TYPES_PENDING);
    return TriggerService.getTriggerTypes().then((res) => {
      if (res.status === 204) {
        commit(C.TRIGGERS_TYPES_NO_CONTENT);
      } else {
        commit(C.TRIGGERS_TYPES_SUCCESS, res.data);
      }
    }).catch(() => {
      commit(C.TRIGGERS_TYPES_FAILURE);
    });
  },

  /**
   * @param commit
   * @param getters
   * @param domainId
   */
  getTemplates({ commit, getters }, domainId) {
    commit(C.TRIGGERS_TEMPLATES_PENDING);
    TriggerService.getTemplates(domainId).then((res) => {
      if (res.status === 204) {
        commit(C.TRIGGERS_TEMPLATES_NO_CONTENT);
      } else {
        const options = keyBy(getters.getAttachmentOptions, 'id');
        res.data.forEach((template) => {
          const attachments = [];
          template.attachments.forEach((attachment) => {
            attachments.push({ ...options[attachment.type] });
          });
          template.attachments = attachments;
        });
        commit(C.TRIGGERS_TEMPLATES_SUCCESS, res.data);
      }
    }).catch(() => {
      commit(C.TRIGGERS_TEMPLATES_FAILURE);
    });
  },

  /**
   * @param commit
   * @param template
   */
  createTemplate({ commit }, template) {
    commit(C.TRIGGERS_TEMPLATE_SAVE_PENDING);
    return TriggerService.createTemplate(template).then(() => {
      commit(C.TRIGGERS_TEMPLATE_SAVE_SUCCESS);
    }).catch(() => {
      commit(C.TRIGGERS_TEMPLATE_SAVE_FAILURE);
    });
  },

  /**
   * @param commit
   * @param template
   */
  editTemplate({ commit }, template) {
    commit(C.TRIGGERS_TEMPLATE_SAVE_PENDING);
    return TriggerService.editTemplate(template).then(() => {
      commit(C.TRIGGERS_TEMPLATE_SAVE_SUCCESS);
    }).catch(() => {
      commit(C.TRIGGERS_TEMPLATE_SAVE_FAILURE);
    });
  },

  /**
   * @param commit
   * @param dispatch
   * @param domain
   */
  setRuleDomain({ commit }, domain) {
    commit(C.TRIGGERS_RULES_DOMAIN_SET, domain);
  },

  /**
   *
   * @param dispatch
   * @param triggerTypeId
   */
  setRuleTriggerType({ commit, dispatch }, triggerType) {
    commit(C.TRIGGERS_RULES_TRIGGER_TYPE_SET, triggerType);
    dispatch('getRevisions');
    dispatch('getTemplatesForRules');
    dispatch('getSettings');
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   */
  getRules({ commit, state, dispatch }) {
    if (!state.rules.domain) {
      return;
    }
    commit(C.TRIGGERS_RULES_PENDING);
    TriggerService.getRulesForDomain(
      state.rules.domain.id,
      state.rules.currentRevision,
      state.rules.triggerType.id,
    ).then((res) => {
      if (res.status === 204) {
        commit(C.TRIGGERS_RULES_NO_CONTENT);
      } else {
        commit(C.TRIGGERS_RULES_SUCCESS, res.data);
        if (!state.rules.currentRevision) {
          dispatch('setRevision', res.data.revision);
        }
      }
    }).catch(() => {
      commit(C.TRIGGERS_RULES_FAILURE);
    });
  },

  /**
   * @param commit
   * @param state
   */
  getSettings({ commit, state }) {
    commit(C.TRIGGERS_RULES_SETTINGS_PENDING);
    TriggerService.getSettings(
      state.rules.domain.id,
      state.rules.triggerType.id,
    ).then((res) => {
      if (res.status === 204) {
        commit(C.TRIGGERS_RULES_SETTINGS_NO_CONTENT);
      } else {
        res.data.forEach((item) => {
          if (typeof item.data === 'string') {
            try {
              const result = JSON.parse(item.data);
              const type = Object.prototype.toString.call(result);
              if (type === '[object Object]' || type === '[object Array]') {
                item.data = result;
              }
            } catch (err) {
              item.data = 'invalid';
            }
          } else {
            item.data = 'invalid';
          }
        });
        commit(C.TRIGGERS_RULES_SETTINGS_SUCCESS, res.data);
      }
    }).catch(() => {
      commit(C.TRIGGERS_RULES_SETTINGS_FAILURE);
    });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param setting
   */
  createSetting({ commit, state, dispatch }, setting) {
    commit(C.TRIGGERS_RULES_SETTINGS_ACTION_PENDING);
    return TriggerService.createSetting(
      state.rules.domain.id,
      state.rules.triggerType.id,
      setting,
    ).then(() => {
      commit(C.TRIGGERS_RULES_SETTINGS_ACTION_SUCCESS);
      dispatch('getSettings');
      Snackbar.success(dispatch, 'The new exception has been created');
      return Promise.resolve();
    }).catch(() => {
      commit(C.TRIGGERS_RULES_SETTINGS_ACTION_FAILURE);
      Snackbar.error(dispatch, 'Unable to save the new exception');
      return Promise.reject();
    });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param setting
   */
  updateSetting({ commit, state, dispatch }, setting) {
    commit(C.TRIGGERS_RULES_SETTINGS_ACTION_PENDING);
    return TriggerService.updateSetting(
      state.rules.domain.id,
      state.rules.triggerType.id,
      setting,
    ).then(() => {
      commit(C.TRIGGERS_RULES_SETTINGS_ACTION_SUCCESS);
      dispatch('getSettings');
      Snackbar.success(dispatch, 'The exception has been updated');
      return Promise.resolve();
    }).catch(() => {
      commit(C.TRIGGERS_RULES_SETTINGS_ACTION_FAILURE);
      Snackbar.error(dispatch, 'Unable to update the exception');
      return Promise.reject();
    });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param setting
   */
  deleteSetting({ commit, state, dispatch }, setting) {
    commit(C.TRIGGERS_RULES_SETTINGS_DELETE_PENDING);
    return TriggerService.deleteSetting(
      state.rules.domain.id,
      state.rules.triggerType.id,
      setting,
    ).then(() => {
      commit(C.TRIGGERS_RULES_SETTINGS_DELETE_SUCCESS);
      dispatch('getSettings');
      Snackbar.success(dispatch, 'The exception has been deleted');
      return Promise.resolve();
    }).catch(() => {
      commit(C.TRIGGERS_RULES_SETTINGS_DELETE_FAILURE);
      Snackbar.error(dispatch, 'Unable to delete the exception');
      return Promise.reject();
    });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param setting
   */
  activateSetting({ commit, state, dispatch }, setting) {
    commit(C.TRIGGERS_RULES_SETTINGS_DELETE_PENDING);
    return TriggerService.activateSetting(state.rules.domain.id, setting).then(() => {
      commit(C.TRIGGERS_RULES_SETTINGS_DELETE_SUCCESS);
      dispatch('getSettings');
      Snackbar.success(dispatch, 'The exception has been activated');
      return Promise.resolve();
    }).catch(() => {
      commit(C.TRIGGERS_RULES_SETTINGS_DELETE_FAILURE);
      Snackbar.error(dispatch, 'Unable to restore the exception');
      return Promise.reject();
    });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param settingId
   */
  getLogs({ commit, state, dispatch }, settingId) {
    commit(C.TRIGGERS_RULES_SETTINGS_LOGS_PENDING);
    TriggerService.getLogs(state.rules.domain.id, settingId).then((res) => {
      commit(C.TRIGGERS_RULES_SETTINGS_LOGS_SUCCESS, res.data);
    }).catch(() => {
      commit(C.TRIGGERS_RULES_SETTINGS_LOGS_FAILURE);
      Snackbar.error(dispatch, 'Unable to fetch the logs for this exception');
    });
  },

  /**
   * @param commit
   * @param domainId
   */
  getTemplatesForRules({ commit, state }) {
    commit(C.TRIGGERS_RULES_TEMPLATES_PENDING);
    return TriggerService.getTemplates(state.rules.domain.id).then((res) => {
      if (res.status === 204) {
        commit(C.TRIGGERS_RULES_TEMPLATES_NO_CONTENT);
      } else {
        commit(C.TRIGGERS_RULES_TEMPLATES_SUCCESS, res.data);
      }
    }).catch(() => {
      commit(C.TRIGGERS_RULES_TEMPLATES_FAILURE);
    });
  },

  /**
   * @param commit
   * @param state
   * @param template
   */
  addTemplate({ commit, state }, template) {
    template.order = state.rules.rules.item.templates.length + 1;
    commit(C.TRIGGERS_RULES_TEMPLATES_ADD, template);
  },

  /**
   * @param commit
   * @param from
   * @param templateIds
   */
  swapTemplate({ commit, state }, templateIds) {
    const from = state.rules.rules.item.templates.findIndex(
      rule => rule.id === templateIds.from,
    );
    const to = state.rules.rules.item.templates.findIndex(
      rule => rule.id === templateIds.to,
    );
    commit(C.TRIGGERS_RULES_TEMPLATES_SWAP_ORDER, { from, to });
  },

  /**
   * @param commit
   * @param state
   * @param templateId
   */
  deleteTemplate({ commit, state }, templateId) {
    const index = state.rules.rules.item.templates.findIndex(rule => rule.id === templateId);
    commit(C.TRIGGERS_RULES_TEMPLATES_DELETE, {
      index,
      order: state.rules.rules.item.templates[index].order,
    });
  },

  /**
   * @param commit
   * @param state
   * @param valid
   * @param template
   */
  updateRuleTemplate({ commit, state }, template) {
    const index = state.rules.rules.item.templates.findIndex(t => t.id === template.id);
    commit(C.TRIGGERS_RULES_TEMPLATES_UPDATE, { index, template });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   */
  createRevision({ commit, state, dispatch }) {
    commit(C.TRIGGERS_RULES_SAVE_PENDING);
    TriggerService.createRevision(
      state.rules.domain.id,
      state.rules.triggerType.id,
      state.rules.rules.item.templates,
    ).then(() => {
      commit(C.TRIGGERS_RULES_SAVE_SUCCESS);
      dispatch('getRevisions');
    }).catch(() => {
      commit(C.TRIGGERS_RULES_SAVE_FAILURE);
    });
  },

  /**
   * @param commit
   */
  resetRevision({ commit }) {
    commit(C.TRIGGERS_RULES_REVISIONS_CURRENT);
  },

  /**
   * @param commit
   * @param dispatch
   * @param revision
   */
  setRevision({ commit, dispatch }, revision) {
    commit(C.TRIGGERS_RULES_REVISIONS_CURRENT, revision);
    router.replace({
      ...router.currentRoute,
      query: {
        ...router.currentRoute.query,
        revision,
      },
    });
    dispatch('getRules');
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   */
  getRevisions({ commit, state, dispatch }) {
    commit(C.TRIGGERS_RULES_REVISIONS_PENDING);
    TriggerService.getRevisions(state.rules.domain.id, state.rules.triggerType.id).then((res) => {
      if (res.status === 204) {
        commit(C.TRIGGERS_RULES_REVISIONS_NO_CONTENT);
        dispatch('setRevision', 1);
      } else {
        commit(C.TRIGGERS_RULES_REVISIONS_SUCCESS, res.data);
        dispatch('setRevision', res.data[0].revision);
      }
    }).catch(() => {
      commit(C.TRIGGERS_RULES_REVISIONS_FAILURE);
    });
  },

  /**
   * @param commit
   */
  getPreviewData({ commit }) {
    commit(C.TRIGGERS_LISTS_PREVIEW_PENDING);
    TriggerService.getPreviewData().then((res) => {
      commit(C.TRIGGERS_LISTS_PREVIEW_SUCCESS, res.data);
    }).catch(() => {
      commit(C.TRIGGERS_LISTS_PREVIEW_FAILURE);
    });
  },

  /**
   * @param commit
   */
  getOverview({ commit }) {
    commit(C.TRIGGERS_LISTS_OVERVIEW_PENDING);
    TriggerService.getOverview().then((res) => {
      commit(C.TRIGGERS_LISTS_OVERVIEW_SUCCESS, res.data);
    }).catch(() => {
      commit(C.TRIGGERS_LISTS_OVERVIEW_FAILURE);
    });
  },

  /**
   * @param commit
   * @param list
   */
  getList({ commit }, list) {
    commit(C.TRIGGERS_LISTS_LIST_PENDING);
    TriggerService.getList(list).then((res) => {
      if (res.status === 204) {
        commit(C.TRIGGERS_LISTS_LIST_SUCCESS, []);
      } else {
        commit(C.TRIGGERS_LISTS_LIST_SUCCESS, res.data);
      }
    }).catch(() => {
      commit(C.TRIGGERS_LISTS_LIST_FAILURE);
    });
  },

  /**
   * @param commit
   * @param booking
   */
  getPaymentMessages({ commit }, booking) {
    commit(C.TRIGGERS_LISTS_MESSAGES_PENDING, booking);
    TriggerService.getPaymentMessages(booking.bookingId).then((res) => {
      if (res.status === 204) {
        commit(C.TRIGGERS_LISTS_MESSAGES_SUCCESS, []);
      } else {
        commit(C.TRIGGERS_LISTS_MESSAGES_SUCCESS, res.data);
      }
    }).catch(() => {
      commit(C.TRIGGERS_LISTS_MESSAGES_FAILURE);
    });
  },

  /**
   * @param commit
   * @param filters
   */
  updateListFilters({ commit }, filters) {
    commit(C.TRIGGERS_LISTS_FILTERS_UPDATE, filters);
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/triggers/actions.js